/**
 * Configurable keyboard shortcuts ("Control+Shift+Space"). Matching is `KeyboardEvent.code`-based for
 * physical keys (Space, Period, letters, digits, function keys) so it is layout-independent, with a
 * `key` fallback for anything else.
 */
export interface ShortcutCombo {
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  meta: boolean;
  /** Normalised `KeyboardEvent.code` (e.g. `Space`, `Period`, `KeyA`, `Digit1`, `F8`). */
  code: string;
}

/** Subset of KeyboardEvent used for matching, so tests and non-DOM callers can pass plain objects. */
export interface KeyEventLike {
  code: string;
  key: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

const MODIFIER_TOKENS: Record<string, keyof Omit<ShortcutCombo, 'code'>> = {
  control: 'ctrl',
  ctrl: 'ctrl',
  shift: 'shift',
  alt: 'alt',
  option: 'alt',
  meta: 'meta',
  cmd: 'meta',
  command: 'meta',
  super: 'meta',
  win: 'meta',
};

const NAMED_CODES: Record<string, string> = {
  space: 'Space',
  spacebar: 'Space',
  ' ': 'Space',
  period: 'Period',
  '.': 'Period',
  comma: 'Comma',
  ',': 'Comma',
  slash: 'Slash',
  '/': 'Slash',
  backslash: 'Backslash',
  '\\': 'Backslash',
  semicolon: 'Semicolon',
  ';': 'Semicolon',
  quote: 'Quote',
  "'": 'Quote',
  backquote: 'Backquote',
  '`': 'Backquote',
  minus: 'Minus',
  '-': 'Minus',
  equal: 'Equal',
  '=': 'Equal',
  bracketleft: 'BracketLeft',
  '[': 'BracketLeft',
  bracketright: 'BracketRight',
  ']': 'BracketRight',
  enter: 'Enter',
  return: 'Enter',
  tab: 'Tab',
  escape: 'Escape',
  esc: 'Escape',
  backspace: 'Backspace',
  delete: 'Delete',
  insert: 'Insert',
  home: 'Home',
  end: 'End',
  pageup: 'PageUp',
  pagedown: 'PageDown',
  arrowup: 'ArrowUp',
  arrowdown: 'ArrowDown',
  arrowleft: 'ArrowLeft',
  arrowright: 'ArrowRight',
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  capslock: 'CapsLock',
  numlock: 'NumLock',
  scrolllock: 'ScrollLock',
  pause: 'Pause',
  printscreen: 'PrintScreen',
  contextmenu: 'ContextMenu',
};

const MODIFIER_CODES = new Set(['ControlLeft', 'ControlRight', 'ShiftLeft', 'ShiftRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight', 'OSLeft', 'OSRight']);

/** Normalises a key token or `KeyboardEvent.code` to the canonical code used by combos. */
export function normalizeKeyToken(token: string): string | undefined {
  const t = token.trim();
  if (!t) return undefined;
  const lower = t.toLowerCase();
  if (NAMED_CODES[lower]) return NAMED_CODES[lower];
  if (/^key[a-z]$/i.test(t)) return `Key${t.slice(3).toUpperCase()}`;
  if (/^digit\d$/i.test(t)) return `Digit${t.slice(5)}`;
  if (/^[a-z]$/i.test(t)) return `Key${t.toUpperCase()}`;
  if (/^\d$/.test(t)) return `Digit${t}`;
  if (/^f([1-9]|1\d|2[0-4])$/i.test(t)) return `F${t.slice(1)}`;
  if (/^numpad/i.test(t)) return `Numpad${t.slice(6)}`;
  if (/^[A-Z][A-Za-z0-9]*$/.test(t)) return t; // already a code-like token (e.g. "Space", "ArrowUp")
  return undefined;
}

/** Parses "Control+Shift+Space" → combo; returns undefined for strings without a non-modifier key. */
export function parseShortcut(text: string): ShortcutCombo | undefined {
  const combo: ShortcutCombo = { ctrl: false, shift: false, alt: false, meta: false, code: '' };
  const parts = text.split('+').map((p) => p.trim()).filter((p) => p.length > 0);
  if (text.trim().endsWith('+') && parts.length > 0 && parts[parts.length - 1] !== '+') parts.push('+');
  for (const part of parts) {
    const mod = MODIFIER_TOKENS[part.toLowerCase()];
    if (mod) {
      combo[mod] = true;
      continue;
    }
    const code = normalizeKeyToken(part === '+' ? 'Equal' : part);
    if (!code || combo.code) return undefined;
    combo.code = code;
  }
  return combo.code ? combo : undefined;
}

function prettyCode(code: string): string {
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  return code;
}

/** Canonical textual form, e.g. "Control+Shift+Space" — stable for storage in preferences. */
export function formatShortcut(combo: ShortcutCombo): string {
  const parts: string[] = [];
  if (combo.ctrl) parts.push('Control');
  if (combo.alt) parts.push('Alt');
  if (combo.shift) parts.push('Shift');
  if (combo.meta) parts.push('Meta');
  parts.push(prettyCode(combo.code));
  return parts.join('+');
}

/** Display form with platform-friendly modifier names. */
export function describeShortcut(text: string, platform: string = typeof navigator === 'undefined' ? '' : navigator.platform): string {
  const combo = parseShortcut(text);
  if (!combo) return text;
  const mac = /mac|iphone|ipad/i.test(platform);
  const parts: string[] = [];
  if (combo.ctrl) parts.push(mac ? '⌃' : 'Ctrl');
  if (combo.alt) parts.push(mac ? '⌥' : 'Alt');
  if (combo.shift) parts.push(mac ? '⇧' : 'Shift');
  if (combo.meta) parts.push(mac ? '⌘' : 'Win');
  parts.push(prettyCode(combo.code));
  return parts.join(mac ? '' : '+');
}

export function isModifierOnlyEvent(event: Pick<KeyEventLike, 'code' | 'key'>): boolean {
  return MODIFIER_CODES.has(event.code) || ['Control', 'Shift', 'Alt', 'Meta', 'OS'].includes(event.key);
}

function eventCode(event: KeyEventLike): string | undefined {
  return normalizeKeyToken(event.code) ?? normalizeKeyToken(event.key);
}

/** True when the keydown event is exactly this combo (modifiers must match exactly). */
export function matchesShortcut(combo: ShortcutCombo | string, event: KeyEventLike): boolean {
  const c = typeof combo === 'string' ? parseShortcut(combo) : combo;
  if (!c) return false;
  if (event.ctrlKey !== c.ctrl || event.shiftKey !== c.shift || event.altKey !== c.alt || event.metaKey !== c.meta) return false;
  return eventCode(event) === c.code;
}

/** For press-and-hold: releasing the main key OR any modifier of the combo ends the hold. */
export function releasesShortcut(combo: ShortcutCombo | string, event: KeyEventLike): boolean {
  const c = typeof combo === 'string' ? parseShortcut(combo) : combo;
  if (!c) return false;
  if (eventCode(event) === c.code) return true;
  if (c.ctrl && (event.key === 'Control' || event.code === 'ControlLeft' || event.code === 'ControlRight')) return true;
  if (c.shift && (event.key === 'Shift' || event.code === 'ShiftLeft' || event.code === 'ShiftRight')) return true;
  if (c.alt && (event.key === 'Alt' || event.code === 'AltLeft' || event.code === 'AltRight')) return true;
  if (c.meta && (event.key === 'Meta' || event.code === 'MetaLeft' || event.code === 'MetaRight')) return true;
  return false;
}

/** Builds a combo from a keydown (used by the settings "press keys" capture). Undefined for modifier-only presses. */
export function comboFromEvent(event: KeyEventLike): ShortcutCombo | undefined {
  if (isModifierOnlyEvent(event)) return undefined;
  const code = eventCode(event);
  if (!code) return undefined;
  return { ctrl: event.ctrlKey, shift: event.shiftKey, alt: event.altKey, meta: event.metaKey, code };
}

/**
 * Shortcuts fire only when the window has focus and the event target is not an editable element —
 * except the conversation composer, which is allowed so a clinician can talk mid-typing.
 */
export function isShortcutAllowedTarget(target: EventTarget | null, isComposer: (el: Element) => boolean): boolean {
  if (!target || !(target instanceof Element)) return true;
  if (isComposer(target)) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  if ((target as HTMLElement).isContentEditable) return false;
  return true;
}

export const DEFAULT_TOGGLE_SHORTCUT = 'Control+Shift+Space';
export const DEFAULT_PUSH_TO_TALK_SHORTCUT = 'Control+Shift+Period';
